import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { adjustToLocalTime } from "../../utils/format-utils";
import { getPaymentMethodLabel } from "../../utils/paymentMethods";

/**
 * Consolidated receipt detail component for all reports
 * Supports conditional columns based on grouping and payment method display
 *
 * @param {Object} props
 * @param {Object} props.data - Receipt data with receipts array
 * @param {'stylist'|'client'|'service'} props.groupBy - Grouping mode (optional)
 * @param {boolean} props.showPaymentMethod - Show payment method column (default: false)
 */
export const ReceiptDetail = ({ data, groupBy, showPaymentMethod = false }) => {
  const receipts = data?.data?.receipts || data?.receipts || [];

  const formatPaymentMethod = (paymentMethod) =>
    getPaymentMethodLabel(paymentMethod);

  if (!receipts || receipts.length === 0) {
    return (
      <Box sx={{ px: 1, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No receipts found for this group.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ mb: 1.25, color: "text.primary" }}
      >
        Receipt details ({receipts.length} transaction
        {receipts.length !== 1 ? "s" : ""})
      </Typography>
      <TableContainer
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "6px",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Table size="small">
          <TableHead
            sx={{
              bgcolor: "secondary.main",
              borderBottom: "1px solid",
              borderColor: "primary.main",
              "& .MuiTableCell-head": {
                color: "common.white",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 1,
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableRow>
              <TableCell>Receipt ID</TableCell>
              <TableCell>Date</TableCell>
              {groupBy !== "client" && <TableCell>Client</TableCell>}
              {groupBy !== "stylist" && <TableCell>Stylist</TableCell>}
              <TableCell>Services</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Tip</TableCell>
              <TableCell align="right">Total</TableCell>
              {showPaymentMethod && <TableCell>Payment</TableCell>}
              <TableCell>Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {receipts.map((receipt) => (
              <TableRow
                key={receipt.receiptId}
                sx={{
                  "&:nth-of-type(odd)": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "#1b1b1e" : "#fafafa",
                  },
                  "&:nth-of-type(even)": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "#141416" : "transparent",
                  },
                  "& td": {
                    py: 1,
                    verticalAlign: "top",
                    color: "text.primary",
                    borderColor: "divider",
                  },
                }}
              >
                <TableCell>{receipt.receiptId}</TableCell>
                <TableCell>
                  {adjustToLocalTime({ value: receipt.createdAt })}
                </TableCell>
                {groupBy !== "client" && (
                  <TableCell>{receipt.clientName || "Walk-in"}</TableCell>
                )}
                {groupBy !== "stylist" && (
                  <TableCell>{receipt.stylistName || "Unassigned"}</TableCell>
                )}
                <TableCell>
                  {receipt.services && receipt.services.length > 0 ? (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {receipt.services.map((service, idx) => (
                        <Chip
                          key={`${receipt.receiptId}-${service.serviceId || service.serviceName}-${idx}`}
                          label={`${service.serviceName} ($${Number(service.price || 0).toFixed(2)})`}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No services
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  ${Number(receipt.amount || 0).toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  ${Number(receipt.tip || 0).toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  <Typography component="span" variant="body2" fontWeight={700}>
                    ${Number(receipt.total || 0).toFixed(2)}
                  </Typography>
                </TableCell>
                {showPaymentMethod && (
                  <TableCell>
                    {formatPaymentMethod(receipt.paymentMethod)}
                  </TableCell>
                )}
                <TableCell>{receipt.locationName || "Unknown"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
